import { FC } from 'react';

import styled from '@emotion/styled';
import { IdType } from 'store/idType/idTypeAPI';
import { IdentityDocument } from 'store/candidate/types';
import { ReadonlyField as BaseRreadonlyField, BcCheckbox } from 'components';

import { DateField, Divider, SelectField, TextField, TnCCheckbox } from '@britishcouncil/react-solas-ors3';
import { FormFields } from '@britishcouncil/react-forms';
import { BcAlert } from '@britishcouncil/react-solas';

export const IdentityDocumentFields: FC<{
  fields: FormFields<IdentityDocument>;
  disabled: boolean;
  idTypes: IdType[] | undefined;
}> = ({ fields, disabled = true, idTypes }) => {
  return (
    <>
      {disabled ? (
        <ReadonlyFields fields={fields} idTypes={idTypes || []} />
      ) : (
        <EditableForm fields={fields} idTypes={idTypes || []} />
      )}
      <BcCheckbox {...fields.confirmationSelected}>I confirm the above details match my ID document</BcCheckbox>
      <Divider />
    </>
  );
};

const Warnings = () => (
  <BcAlert type="note" title="Note">
    <p>
      Please fill in accurately your identification document number details. You must bring the ID document indicated
      above to the exam. This is the only form of identity that will be accepted on the exam day. The document must be
      valid/not expired at registration and on the exam day.
    </p>
  </BcAlert>
);

const EditableForm: FC<{ fields: FormFields<IdentityDocument>; idTypes: IdType[] }> = ({ fields, idTypes }) => {
  const selectedDocumentType = idTypes.find((x) => x.id === fields.selectedIdentificationDocument.value);
  return (
    <>
      <SelectField
        label="ID document *"
        {...fields.selectedIdentificationDocument}
        options={idTypes || []}
        showColon={false}
      />
      <Warnings />
      <TextField label="ID document number *" {...fields.identificationDocNo} showColon={false} />

      {selectedDocumentType && !selectedDocumentType.mustHaveExpiryDate && (
        <TnCCheckbox {...fields.identificationDocumentHasNoExpiryDate}>
          My national ID does not have an expiry date
        </TnCCheckbox>
      )}

      {!fields.identificationDocumentHasNoExpiryDate.value && (
        <DateField label="ID document expiry date *" {...fields.identificationDocExpiryDate} showColon={false} />
      )}
    </>
  );
};

const ReadonlyFields: FC<{ fields: FormFields<IdentityDocument>; idTypes: IdType[] }> = ({ fields, idTypes }) => (
  <>
    <ReadonlyField label="ID document *">
      {idTypes.find((x) => x.id === fields.selectedIdentificationDocument.value)?.name || ''}
    </ReadonlyField>
    <Warnings />
    <ReadonlyField label="ID document number *">{fields.identificationDocNo.value}</ReadonlyField>
    {!fields.identificationDocumentHasNoExpiryDate.value ? (
      <ReadonlyField label="ID document expiry date *">{fields.identificationDocExpiryDate.value}</ReadonlyField>
    ) : (
      <ReadonlyField label="My national ID does not have an expiry date">Yes</ReadonlyField>
    )}
  </>
);

const ReadonlyField = styled(BaseRreadonlyField)`
  dt {
    width: 55%;
  }
`;
